import React, { useState, useEffect } from 'react';
import { FaFilter, FaSearch, FaCheckCircle } from 'react-icons/fa';
import FraudAlertCard from '../components/fraud/FraudAlertCard';
import { supabase } from '../services/supabase';
import adminService from '../services/admin.service';
import { useAuth } from '../context/AuthContext';

function FraudPage() {
  const { admin, getAdminToken } = useAuth();
  const [frauds, setFrauds] = useState([]);
  const [filteredFrauds, setFilteredFrauds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterResolved, setFilterResolved] = useState('unresolved');
  const [detecting, setDetecting]           = useState(false);
  const [detectionResult, setDetectionResult] = useState(null);

  useEffect(() => {
    fetchFrauds();

    // Real-time subscription
    const subscription = supabase
      .channel('fraud_logs_changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'fraud_logs' },
        (payload) => {
          console.log('New fraud detected:', payload);
          fetchFrauds();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterFrauds();
  }, [filterSeverity, filterType, filterResolved, frauds]);

  const runFraudDetection = async () => {
    setDetecting(true);
    setDetectionResult(null);
    let newAlerts = 0;

    const now     = new Date();
    const today   = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const last7d  = new Date(now - 7  * 24 * 3600000).toISOString();
    const last30d = new Date(now - 30 * 24 * 3600000).toISOString();

    try {
      // Son 30 günün bütün scan-ları
      const { data: scans } = await supabase
        .from('point_logs')
        .select('user_id, user_name, business_id, amount_tier, created_at')
        .gte('created_at', last30d)
        .order('created_at', { ascending: true });

      if (!scans?.length) { setDetecting(false); setDetectionResult({ newAlerts: 0 }); return; }

      // Bu gün artıq var olan həll olunmamış logları yüklə (dublikat önlə)
      const { data: existingRes } = await supabase.rpc('admin_get_fraud_logs', {
        p_admin_token: getAdminToken(), p_unresolved_only: true, p_since: today,
      });
      const existing = existingRes?.success ? existingRes.rows : [];

      const seen = new Set(
        (existing || []).map(e => `${e.user_id || ''}-${e.fraud_type}-${e.business_id || ''}`)
      );

      const logFraud = async (entry) => {
        const key = `${entry.user_id || ''}-${entry.fraud_type}-${entry.business_id || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        const { data } = await supabase.rpc('admin_insert_fraud_log', { p_admin_token: getAdminToken(), p_entry: entry });
        if (data?.success) newAlerts++;
      };

      // ── İSTİFADƏÇİ BAZLI ANALİZ ───────────────────────────────────────────
      const byUser = {};
      scans.forEach(s => {
        if (!s.user_id) return;
        if (!byUser[s.user_id]) byUser[s.user_id] = { name: s.user_name || 'İstifadəçi', scans: [] };
        byUser[s.user_id].scans.push(s);
      });

      for (const [userId, u] of Object.entries(byUser)) {
        const all = u.scans;

        // ── SSENARI 1: Bir gündə 4+ fərqli kafe (kassir tanış fraud) ─────────
        const byDate = {};
        all.forEach(s => {
          const d = s.created_at.slice(0, 10);
          if (!byDate[d]) byDate[d] = new Set();
          byDate[d].add(s.business_id);
        });
        for (const [date, bizsSet] of Object.entries(byDate)) {
          if (bizsSet.size >= 4) {
            await logFraud({
              user_id:    userId,
              fraud_type: 'multi_business_abuse',
              severity:   'high',
              details: {
                date,
                business_count: bizsSet.size,
                description: `${u.name} bir gündə (${date}) ${bizsSet.size} fərqli kafedə QR scan etdi`,
              },
            });
          }
        }

        // ── SSENARI 2: 15 dəqiqədən az aralıqda fərqli kafe ─────────────────
        for (let i = 1; i < all.length; i++) {
          const prev = all[i - 1];
          const curr = all[i];
          if (prev.business_id === curr.business_id) continue;
          const diffMin = (new Date(curr.created_at) - new Date(prev.created_at)) / 60000;
          if (diffMin > 0 && diffMin < 15) {
            await logFraud({
              user_id:    userId,
              fraud_type: 'rapid_checkins',
              severity:   'high',
              details: {
                interval_minutes: Math.round(diffMin),
                description: `${u.name} ${Math.round(diffMin)} dəqiqə ərzində 2 fərqli kafedə scan etdi`,
              },
            });
            break; // istifadəçi üçün 1 alert kifayətdir
          }
        }

        // ── SSENARI 3: Daim maksimum tier (80%+ scan-ı 50₼+) ────────────────
        if (all.length >= 5) {
          const maxCount = all.filter(s => s.amount_tier === '50+').length;
          const pct = Math.round((maxCount / all.length) * 100);
          if (pct >= 80) {
            await logFraud({
              user_id:    userId,
              fraud_type: 'suspicious_tier_pattern',
              severity:   'medium',
              details: {
                total_scans:    all.length,
                max_tier_count: maxCount,
                percentage:     pct,
                description:    `${u.name} scan-larının ${pct}%-i maksimum tier (50₼+)`,
              },
            });
          }
        }

        // ── SSENARI 4: Son 7 gündə 8+ fərqli kafə (yüksək xal sürəti) ───────
        const recent = all.filter(s => s.created_at >= last7d);
        const uniqBiz7 = new Set(recent.map(s => s.business_id)).size;
        if (uniqBiz7 >= 8) {
          await logFraud({
            user_id:    userId,
            fraud_type: 'high_velocity_points',
            severity:   'medium',
            details: {
              unique_businesses_7d: uniqBiz7,
              scan_count_7d:        recent.length,
              description:          `${u.name} son 7 gündə ${uniqBiz7} fərqli kafedə scan etdi`,
            },
          });
        }

        // ── SSENARI 5: Eyni saatda 3+ fərqli kafe (fiziki qeyri-mümkün) ──────
        const byHour = {};
        all.forEach(s => {
          const h = s.created_at.slice(0, 13); // "2026-05-02T14"
          if (!byHour[h]) byHour[h] = new Set();
          byHour[h].add(s.business_id);
        });
        for (const [hour, bizSet] of Object.entries(byHour)) {
          if (bizSet.size >= 3) {
            await logFraud({
              user_id:    userId,
              fraud_type: 'impossible_location',
              severity:   'high',
              details: {
                hour,
                business_count: bizSet.size,
                description: `${u.name} 1 saat ərzində ${bizSet.size} fərqli kafedə scan etdi — fiziki qeyri-mümkün`,
              },
            });
          }
        }
      }

      // ── KAFƏ BAZLI ANALİZ ─────────────────────────────────────────────────
      const todayScans = scans.filter(s => s.created_at >= today);
      const byBiz = {};
      todayScans.forEach(s => {
        if (!s.business_id) return;
        if (!byBiz[s.business_id]) byBiz[s.business_id] = { users: new Set(), maxTier: 0, total: 0 };
        byBiz[s.business_id].users.add(s.user_id);
        byBiz[s.business_id].total++;
        if (s.amount_tier === '50+') byBiz[s.business_id].maxTier++;
      });

      // ── SSENARI 6: Şübhəli kafe — çox istifadəçini max tier-da oxudur ─────
      for (const [bizId, d] of Object.entries(byBiz)) {
        const userCount  = d.users.size;
        const maxTierPct = userCount > 0 ? Math.round((d.maxTier / d.total) * 100) : 0;
        if (userCount >= 10 && maxTierPct >= 70) {
          await logFraud({
            business_id: bizId,
            user_id:     null,
            fraud_type:  'suspicious_business',
            severity:    'medium',
            details: {
              user_count:          userCount,
              max_tier_percentage: maxTierPct,
              description:         `Bu kafe bu gün ${userCount} istifadəçini ${maxTierPct}% nisbətdə max tier-da oxutdu`,
            },
          });
        }
      }

      // ── SSENARI 7: Eyni istifadəçi birdən çox hesabla (oxşar davranış) ────
      // user_name oxşarlığı ilə aşkarlama (eyni ad, fərqli user_id)
      const nameToUsers = {};
      Object.entries(byUser).forEach(([uid, u]) => {
        const key = (u.name || '').toLowerCase().trim();
        if (!key || key === 'istifadəçi') return;
        if (!nameToUsers[key]) nameToUsers[key] = [];
        nameToUsers[key].push(uid);
      });
      for (const [name, uids] of Object.entries(nameToUsers)) {
        if (uids.length >= 2) {
          await logFraud({
            user_id:    uids[0],
            fraud_type: 'duplicate_account',
            severity:   'low',
            details: {
              matching_name:  name,
              account_count:  uids.length,
              user_ids:       uids,
              description:    `"${name}" adı ilə ${uids.length} fərqli hesab aktiv şəkildə scan edir`,
            },
          });
        }
      }

      setDetectionResult({ newAlerts });
      await fetchFrauds();

    } catch (e) {
      console.error('Fraud detection error:', e);
      setDetectionResult({ error: true });
    } finally {
      setDetecting(false);
    }
  };

  const fetchFrauds = async () => {
    try {
      const { data: res, error } = await supabase.rpc('admin_get_fraud_logs', { p_admin_token: getAdminToken() });
      if (error) throw error;
      if (!res?.success) throw new Error(res?.error);
      const data = res.rows;

      // User və business məlumatlarını əlavə et
      const fraudsWithDetails = await Promise.all(
        data.map(async (fraud) => {
          let userData = null;
          let businessData = null;

          if (fraud.user_id) {
            const { data: briefRes } = await supabase.rpc('admin_get_user_brief', { p_admin_token: getAdminToken(), p_user_id: fraud.user_id });
            userData = briefRes?.success ? briefRes : null;
          }

          if (fraud.business_id) {
            const { data: business } = await supabase
              .from('businesses')
              .select('name')
              .eq('id', fraud.business_id)
              .single();
            businessData = business;
          }

          return {
            ...fraud,
            user_name: userData?.full_name,
            user_username: userData?.username,
            user_email: userData?.email,
            business_name: businessData?.name,
          };
        })
      );

      setFrauds(fraudsWithDetails);
    } catch (error) {
      console.error('Error fetching frauds:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFrauds = () => {
    let filtered = frauds;

    // Severity filter
    if (filterSeverity !== 'all') {
      filtered = filtered.filter((f) => f.severity === filterSeverity);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((f) => f.fraud_type === filterType);
    }

    // Resolved filter
    if (filterResolved === 'resolved') {
      filtered = filtered.filter((f) => f.resolved === true);
    } else if (filterResolved === 'unresolved') {
      filtered = filtered.filter((f) => f.resolved === false || !f.resolved);
    }

    setFilteredFrauds(filtered);
  };

  const handleViewDetails = (fraud) => {
    alert(`Fraud Details:\n\n${JSON.stringify(fraud, null, 2)}`);
  };

  const handleBanUser = async (fraud) => {
    if (!fraud.user_id) {
      alert('User ID tapılmadı!');
      return;
    }

    if (!window.confirm(`${fraud.user_name} fraud səbəbi ilə ban edilsin?`)) {
      return;
    }

    try {
      // Ban et
      const { error } = await supabase.from('user_bans').insert({
        user_id: fraud.user_id,
        reason: `Fraud: ${fraud.fraud_type}`,
        banned_by: admin.id,
      });

      if (error) throw error;

      // Fraud-u həll olunmuş kimi işarələ
      await supabase.rpc('admin_resolve_fraud', { p_admin_token: getAdminToken(), p_fraud_id: fraud.id });

      // Log yaz
      await adminService.logAction(
        admin.id,
        'ban_user_fraud',
        'user',
        fraud.user_id,
        { fraud_id: fraud.id }
      );

      alert('User uğurla ban edildi!');
      fetchFrauds();
    } catch (error) {
      console.error('Error banning user:', error);
      alert('Xəta baş verdi!');
    }
  };

  const handleResolve = async (fraud) => {
    if (!window.confirm('Bu fraud alert-i həll olunmuş kimi işarələyirsiniz?')) {
      return;
    }

    try {
      const { data } = await supabase.rpc('admin_resolve_fraud', { p_admin_token: getAdminToken(), p_fraud_id: fraud.id });
      if (!data?.success) throw new Error(data?.error || 'Xəta baş verdi');

      // Log yaz
      await adminService.logAction(
        admin.id,
        'resolve_fraud',
        'fraud',
        fraud.id
      );

      alert('Fraud uğurla həll edildi!');
      fetchFrauds();
    } catch (error) {
      console.error('Error resolving fraud:', error);
      alert('Xəta baş verdi!');
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  const highPriority = filteredFrauds.filter((f) => f.severity === 'high' && !f.resolved);
  const mediumPriority = filteredFrauds.filter((f) => f.severity === 'medium' && !f.resolved);
  const lowPriority = filteredFrauds.filter((f) => f.severity === 'low' && !f.resolved);
  const resolved = filteredFrauds.filter((f) => f.resolved);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={styles.title}>🚨 Fraud Detection Center</h1>
          <p style={styles.subtitle}>Şübhəli fəaliyyətləri izləyin və idarə edin</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <button
            onClick={runFraudDetection}
            disabled={detecting}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 10, border: 'none',
              background: detecting ? '#ccc' : '#E63950', color: '#fff',
              fontWeight: 700, fontSize: 14, cursor: detecting ? 'not-allowed' : 'pointer',
              boxShadow: detecting ? 'none' : '0 2px 8px rgba(230,57,80,0.3)',
            }}
          >
            <FaSearch style={{ fontSize: 13 }} />
            {detecting ? 'Analiz edilir...' : 'Fraud Analizi Başlat'}
          </button>
          {detectionResult && !detectionResult.error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600,
              color: detectionResult.newAlerts > 0 ? '#dc2626' : '#16a34a',
            }}>
              <FaCheckCircle />
              {detectionResult.newAlerts > 0
                ? `${detectionResult.newAlerts} yeni şübhəli hal aşkarlandı`
                : 'Yeni şübhəli hal yoxdur'}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #F44336' }}>
          <span style={styles.statLabel}>YÜKSƏK:</span>
          <span style={{ ...styles.statValue, color: '#F44336' }}>
            {frauds.filter((f) => f.severity === 'high' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #FF9800' }}>
          <span style={styles.statLabel}>ORTA:</span>
          <span style={{ ...styles.statValue, color: '#FF9800' }}>
            {frauds.filter((f) => f.severity === 'medium' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #FFC107' }}>
          <span style={styles.statLabel}>AŞAĞI:</span>
          <span style={{ ...styles.statValue, color: '#FFC107' }}>
            {frauds.filter((f) => f.severity === 'low' && !f.resolved).length}
          </span>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #4CAF50' }}>
          <span style={styles.statLabel}>HƏLL OLUNDU:</span>
          <span style={{ ...styles.statValue, color: '#4CAF50' }}>
            {frauds.filter((f) => f.resolved).length}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <FaFilter style={styles.filterIcon} />
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Severity</option>
            <option value="high">Yüksək</option>
            <option value="medium">Orta</option>
            <option value="low">Aşağı</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Tiplər</option>
            <option value="multi_business_abuse">Çox Kafeli Sui-istifadə</option>
            <option value="rapid_checkins">Sürətli Check-in</option>
            <option value="impossible_location">Fiziki Qeyri-Mümkün Yerlər</option>
            <option value="suspicious_tier_pattern">Şübhəli Xərcləmə Şablonu</option>
            <option value="high_velocity_points">Yüksək Xal Sürəti</option>
            <option value="suspicious_business">Şübhəli Kafe</option>
            <option value="duplicate_account">Dublikat Hesab</option>
            <option value="gps_spoofing">GPS Spoofing</option>
            <option value="spin_abuse">Spin Abuse</option>
            <option value="fake_account">Saxta Hesab</option>
          </select>

          <select
            value={filterResolved}
            onChange={(e) => setFilterResolved(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Hamısı</option>
            <option value="unresolved">Həll olunmamış</option>
            <option value="resolved">Həll olunmuş</option>
          </select>
        </div>
      </div>

      {/* Fraud Alerts */}
      {highPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🔴 YÜKSƏK PRİORİTET ({highPriority.length})</h3>
          {highPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {mediumPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🟡 ORTA PRİORİTET ({mediumPriority.length})</h3>
          {mediumPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {lowPriority.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>🟢 AŞAĞI PRİORİTET ({lowPriority.length})</h3>
          {lowPriority.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {filterResolved === 'all' && resolved.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>✅ HƏLL OLUNMUŞ ({resolved.length})</h3>
          {resolved.map((fraud) => (
            <FraudAlertCard
              key={fraud.id}
              fraud={fraud}
              onViewDetails={handleViewDetails}
              onBanUser={handleBanUser}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}

      {filteredFrauds.length === 0 && (
        <div style={styles.noData}>
          <p>🎉 Heç bir fraud alert yoxdur!</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '30px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  filters: {
    marginBottom: '30px',
  },
  filterGroup: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterIcon: {
    color: '#999',
    fontSize: '18px',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  noData: {
    padding: '60px 20px',
    textAlign: 'center',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default FraudPage;